import { createApi } from '@reduxjs/toolkit/query/react';
import { TAG_TYPES } from '../constants/queryTags';
import baseQueryWithTimeout from './baseQueryWithTimeout';

const { CLAIMS, SAVED_CLAIM, SAVED_UPLOADED_DOCUMENTS } = TAG_TYPES;

const getClaimsBaseUrl = (isV3ApiEnabled) => (isV3ApiEnabled ? '/api/v3/claims' : '/api/claim');
const getClaimsHeaders = (auth0Token, isV3ApiEnabled) => (isV3ApiEnabled ? { customAuthToken: auth0Token } : {});

export const claimApi = createApi({
  reducerPath: 'claimApi',
  baseQuery: baseQueryWithTimeout,
  tagTypes: [CLAIMS, SAVED_CLAIM, SAVED_UPLOADED_DOCUMENTS],
  endpoints: (builder) => ({
    getDashboardClaims: builder.query({
      query: (auth0Token) => ({
        url: '/api/v3/claims/dashboardClaims',
        headers: { customAuthToken: auth0Token }
      })
    }),
    // Post
    getClaims: builder.mutation({
      query: ({ data, auth0Token }) => ({
        url: '/api/v3/claims/claims',
        method: 'POST',
        headers: { customAuthToken: auth0Token },
        body: { Policies: data }
      })
    }),
    getUploadAdditionalDocumentsDraft: builder.query({
      query: (claimNumber) => ({
        url: `/api/claim/GetAdditionalClaimDocumentDraft/${claimNumber}`
      }),
      providesTags: [SAVED_UPLOADED_DOCUMENTS]
    }),
    loadMoreClaims: builder.mutation({
      query: ({ body, auth0Token }) => ({
        url: '/api/v3/claims/loadMoreClaims',
        method: 'POST',
        headers: { customAuthToken: auth0Token },
        body: { ...body, Take: body?.Take ?? 10 }
      })
    }),
    postSaveClaim: builder.mutation({
      query: ({ data, auth0Token, isV3ApiEnabled }) => ({
        url: `${getClaimsBaseUrl(isV3ApiEnabled)}/saveClaim`,
        method: 'POST',
        headers: getClaimsHeaders(auth0Token, isV3ApiEnabled),
        body: data
      }),
      invalidatesTags: [CLAIMS]
    }),
    postClaimAttachment: builder.mutation({
      query: (data) => ({
        url: '/api/claim/claimAttachment',
        method: 'POST',
        body: data
      }),
      invalidatesTags: [SAVED_CLAIM]
    }),
    postSubmitClaim: builder.mutation({
      query: (data) => ({
        url: '/api/claim/submitClaim',
        method: 'POST',

        body: data
      }),
      invalidatesTags: [CLAIMS]
    }),
    // Get
    getSavedClaims: builder.query({
      query: ({ contactId, partnerNumber, auth0Token, isV3ApiEnabled }) => ({
        url: isV3ApiEnabled
          ? `${getClaimsBaseUrl(isV3ApiEnabled)}/savedClaims`
          : `${getClaimsBaseUrl(isV3ApiEnabled)}/savedClaims/${contactId}/${partnerNumber}`,
        headers: getClaimsHeaders(auth0Token, isV3ApiEnabled)
      }),
      providesTags: [CLAIMS]
    }),
    getSavedClaim: builder.query({
      query: ({ customerClaimId, auth0Token, isV3ApiEnabled }) => ({
        url: isV3ApiEnabled
          ? `/api/v3/claims/savedClaim/${customerClaimId}`
          : `/api/claim/savedClaim/${customerClaimId}`,
        headers: getClaimsHeaders(auth0Token, isV3ApiEnabled)
      }),
      providesTags: [SAVED_CLAIM]
    }),
    getClaimAttachment: builder.query({
      query: (docPath) => ({
        url: `/api/claim/claimAttachment/${docPath}`,
        responseHandler: (response) => response.blob()
      })
    }),
    getClaimRemittance: builder.query({
      query: ({ claimNumber, auth0Token }) => ({
        url: `/api/v3/claims/claimRemittance/${claimNumber}`,
        headers: { customAuthToken: auth0Token }
      })
    }),
    getServiceProviders: builder.query({
      query: (auth0Token) => ({
        url: '/api/v3/claims/serviceproviders',
        headers: { customAuthToken: auth0Token }
      })
    }),
    // Delete
    deleteClaim: builder.mutation({
      query: ({ claimNo, auth0Token, isV3ApiEnabled }) => ({
        url: `${getClaimsBaseUrl(isV3ApiEnabled)}/deleteClaim/${claimNo}`,
        method: 'DELETE',
        headers: getClaimsHeaders(auth0Token, isV3ApiEnabled)
      }),
      invalidatesTags: [CLAIMS]
    }),
    deleteClaimAttachment: builder.mutation({
      query: (claimFieldId) => ({
        url: `/api/claim/removeClaimAttachment/${claimFieldId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [SAVED_CLAIM]
    }),
    saveUploadAdditionalDocuments: builder.mutation({
      query: (data) => ({
        url: '/api/claim/SaveAdditionalClaimDocumentDraft',
        method: 'POST',
        body: data
      }),
      invalidatesTags: [SAVED_UPLOADED_DOCUMENTS]
    }),
    uploadSelectedClaimDocuments: builder.mutation({
      query: (data) => ({
        url: '/api/claim/uploadSelectedClaimDocuments',
        method: 'POST',
        body: data
      })
    }),
    removeAdditionalClaimAttachment: builder.mutation({
      query: (docPath) => ({
        url: `/api/claim/removeAdditionalClaimAttachment`,
        method: 'POST',
        body: docPath
      }),
      invalidatesTags: [SAVED_UPLOADED_DOCUMENTS]
    }),
    submitAdditionalClaimDocuments: builder.mutation({
      query: (data) => ({
        url: '/api/claim/SubmitAdditionalDocuments',
        method: 'POST',
        body: data
      }),
      invalidatesTags: [CLAIMS]
    })
  })
});

export const {
  useGetDashboardClaimsQuery,
  useGetClaimsMutation,
  useGetSavedClaimsQuery,
  useGetSavedClaimQuery,
  useGetClaimRemittanceQuery,
  useGetUploadAdditionalDocumentsDraftQuery,
  useGetServiceProvidersQuery,
  useLoadMoreClaimsMutation,
  useLazyGetClaimAttachmentQuery,
  usePostSaveClaimMutation,
  usePostSubmitClaimMutation,
  usePostClaimAttachmentMutation,
  useDeleteClaimMutation,
  useDeleteClaimAttachmentMutation,
  useSaveUploadAdditionalDocumentsMutation,
  useUploadSelectedClaimDocumentsMutation,
  useRemoveAdditionalClaimAttachmentMutation,
  useSubmitAdditionalClaimDocumentsMutation
} = claimApi;
